import { useContext, useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { APIContext } from "../../APIContext";
import { db } from "../../lib/firebase";
import Company from "../../lib/uma/companies";
import Client from "../../lib/uma/clients";
import Product, { getProducts } from "../../lib/uma/products";

interface ILoadedState {
  userId: string;
  userEmail: string | null;
  companyId: string | null;
  companyName: string | null;
  byEmployeeEmail: string | null;
  at: number;
  productNames: string[];
}

interface IViewAsConfig {
  shopify_customer_id?: number | string;
  shopify_customer_email?: string;
  storefront_url?: string;
  loaded?: ILoadedState;
}

/**
 * View-as Client tester (Phase 1). Lets an employee mirror a client user's live
 * Shopify access onto the shared "View-as shadow" customer, then browse the
 * storefront as that user in a separate Portal QA browser profile. See
 * docs/plans/phase-1-view-as.md.
 */
export default function ViewAsPage() {
  const context = useContext(APIContext);

  const [companies, setCompanies] = useState<{ docId: string; name: string }[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [config, setConfig] = useState<IViewAsConfig>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<{ type: string; msg: string } | null>(null);

  // Companies (live), products (once), and the View-as config/state doc (live).
  useEffect(() => {
    const companiesQuery = query(collection(db, "companies"), orderBy("company_name", "asc"));
    const unsubCompanies = onSnapshot(
      companiesQuery,
      (snap) =>
        setCompanies(
          snap.docs
            .filter((d) => d.data().company_name)
            .map((d) => ({ docId: d.id, name: d.data().company_name }))
        ),
      (err) => console.error(err)
    );

    const unsubConfig = onSnapshot(
      doc(db, "admin_settings/viewas"),
      (snap) => setConfig(snap.exists() ? (snap.data() as IViewAsConfig) : {}),
      (err) => console.error(err)
    );

    getProducts().then(setProducts, (err) => console.error(err));

    return () => {
      unsubCompanies();
      unsubConfig();
    };
  }, []);

  // Load the selected company's users into the User dropdown.
  useEffect(() => {
    setClients([]);
    setSelectedUserId("");
    if (!selectedCompanyId) return;
    let active = true;
    setClientsLoading(true);
    new Company({ docId: selectedCompanyId })
      .getClients()
      .then(
        (cs) => {
          if (active) setClients(cs.sort((a, b) => (a.email || "").localeCompare(b.email || "")));
        },
        (err) => console.error(err)
      )
      .finally(() => {
        if (active) setClientsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedCompanyId]);

  const spidToName = useMemo(() => {
    const m: { [spid: string]: string } = {};
    products.forEach((p) => {
      m[String(p.shopifyProductId)] = p.productName;
    });
    return m;
  }, [products]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.docId === selectedUserId),
    [clients, selectedUserId]
  );

  // Access preview from the user's CACHED Shopify tags (Actual as of last sync).
  // The authoritative set is read live server-side when Load is pressed.
  const previewProducts = useMemo(() => {
    return String(selectedClient?.associatedShopifyCustomer?.customer?.tags || "")
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.startsWith("access:"))
      .map((t) => {
        const spid = t.slice("access:".length);
        return { spid, name: spidToName[spid] || `Unknown (SPID ${spid})` };
      });
  }, [selectedClient, spidToName]);

  const shadowConfigured = Boolean(config.shopify_customer_id);

  const load = async () => {
    if (!selectedUserId) return;
    setIsLoading(true);
    setStatus(null);
    try {
      const company = companies.find((c) => c.docId === selectedCompanyId);
      const res: any = await context.loadViewAs({
        userId: selectedUserId,
        companyId: selectedCompanyId || null,
        companyName: company?.name || null,
      });
      const d = res?.data || {};
      setStatus({
        type: d.status === "success" ? "success" : "danger",
        msg: d.message || (d.status === "success" ? "Loaded." : "Failed to load."),
      });
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setIsLoading(false);
    }
  };

  const loaded = config.loaded;

  return (
    <div style={{ fontFamily: "Montserrat, sans-serif", marginTop: "3rem" }}>
      <div className="m0 listings-wrapper middle half-width">
        <h1 style={{ fontWeight: "bold" }}>View as Client</h1>
        <p>
          Mirror a client user's live dashboard access onto the shared View-as shadow customer, then
          switch to your <strong>Portal QA</strong> browser window and refresh to see exactly what
          they see &mdash; which nav options appear and which dashboards open.
        </p>

        {/* Shadow status */}
        <div className={`alert ${shadowConfigured ? "alert-info" : "alert-warning"}`}>
          {shadowConfigured ? (
            <>
              Shadow:&nbsp;
              <strong>{config.shopify_customer_email || `#${config.shopify_customer_id}`}</strong>
              {config.storefront_url && (
                <>
                  {" "}
                  &middot;{" "}
                  <a href={config.storefront_url} target="_blank" rel="noopener noreferrer">
                    Open storefront &#8599;
                  </a>
                </>
              )}
            </>
          ) : (
            <>
              The View-as shadow isn&apos;t configured yet. Create a non-Brightfield Shopify customer
              and set <code>admin_settings/viewas.shopify_customer_id</code> (and optionally{" "}
              <code>shopify_customer_email</code> and <code>storefront_url</code>).
            </>
          )}
        </div>

        {/* Company select */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="viewas-company">Client (Company)</label>
          <select
            id="viewas-company"
            className="form-control"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">&mdash; select a company &mdash;</option>
            {companies.map((c) => (
              <option key={c.docId} value={c.docId}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* User select */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="viewas-user">User</label>
          <select
            id="viewas-user"
            className="form-control"
            value={selectedUserId}
            disabled={!selectedCompanyId || clientsLoading}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <option value="">{clientsLoading ? "loading users…" : "— select a user —"}</option>
            {clients.map((c) => (
              <option key={c.docId} value={c.docId}>
                {c.email || `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.docId}
              </option>
            ))}
          </select>
        </div>

        {/* Access preview */}
        {selectedClient && (
          <div style={{ marginTop: "1rem" }}>
            <label>Actual access (from last Shopify sync)</label>
            {previewProducts.length === 0 ? (
              <p className="text-muted">No dashboard access tags on this user.</p>
            ) : (
              <ul>
                {previewProducts.map((p) => (
                  <li key={p.spid}>{p.name}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Load */}
        <div
          style={{ display: "flex", flexDirection: "row", marginTop: "1rem", alignItems: "center" }}
        >
          {status && (
            <div
              className={`alert alert-${status.type}`}
              style={{ marginRight: "1rem", marginBottom: 0 }}
            >
              {status.msg}
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <button
              className="btn btn-primary btn-sm"
              disabled={!selectedUserId || isLoading || !shadowConfigured}
              onClick={load}
            >
              {isLoading ? "Loading…" : "Load onto View-as shadow"}
            </button>
          </div>
        </div>

        {/* Currently loaded */}
        {loaded && (
          <div style={{ marginTop: "1.5rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
            <label>Currently loaded</label>
            <p style={{ marginBottom: "0.25rem" }}>
              <strong>{loaded.companyName || "—"}</strong> ▸ {loaded.userEmail || loaded.userId}
              {loaded.byEmployeeEmail && <> &middot; by {loaded.byEmployeeEmail}</>}
              {loaded.at && <> &middot; {new Date(loaded.at).toLocaleString()}</>}
            </p>
            {loaded.productNames && loaded.productNames.length > 0 && (
              <p className="text-muted" style={{ marginBottom: 0 }}>
                {loaded.productNames.join(" · ")}
              </p>
            )}
            <p style={{ marginTop: "0.5rem" }}>
              &rarr; Switch to your Portal QA window and refresh to apply.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
